import { createAnalyzerService, type AnalyzerOptions } from './analyzer';
import { createClipboardService, createNullClipboardService } from './clipboard';
import { createMemoryStorageService, createStorageService } from './storage';
import { createDetachedThemeService, createThemeService } from './theme';
import type { Services } from './types';

/**
 * The services layer's public surface (docs/ARCHITECTURE.md §4.7).
 *
 * The interfaces live in `./types` and are re-exported here; see the comment in that file
 * for why the split exists. A FEATURE must import them from `@/services/types` — importing
 * this barrel drags `navigator`, `localStorage` and `Worker` into the DOM-free program of
 * `tsconfig.core.json`, and gate 2 says so by name. Everything else imports `@/services`.
 */
export type {
  AnalyzerService,
  ClipboardService,
  Services,
  StorageKey,
  StorageService,
  ThemePreference,
  ThemeService,
} from './types';

export {
  AnalyzerError,
  type AnalyzerFailureReason,
  type AnalyzerOptions,
  createAnalyzerService,
  DEFAULT_WORKER_THRESHOLD_BYTES,
  isAnalyzerError,
  isSuperseded,
} from './analyzer';
export {
  ClipboardError,
  type ClipboardFailureReason,
  createClipboardService,
  isClipboardError,
} from './clipboard';
export {
  type StorageArea,
  createMemoryStorageArea,
  createMemoryStorageService,
  createStorageService,
} from './storage';
export {
  DARK_MEDIA_QUERY,
  createDetachedThemeService,
  createThemeService,
  parseThemePreference,
  type ResolvedTheme,
  THEME_STORAGE_KEY,
} from './theme';
export { ServicesProvider, type ServicesProviderProps, useServices } from './react';

export interface ServicesOptions {
  /**
   * Passed straight to `createAnalyzerService`. The composition root uses it to hand over
   * `WORKER_THRESHOLD_BYTES` from `src/app/config.ts`, which this layer may not import
   * (gate 2 denies `@/app/*` here). Optional, so `createServices()` still matches the
   * zero-argument signature docs/ARCHITECTURE.md §4.7 declares.
   */
  readonly analyzer?: AnalyzerOptions;
}

/**
 * Builds the four real services. Called once, from `src/app/App.tsx`.
 *
 * Construction order is a dependency, not a preference: the theme reads its stored
 * preference through the storage service and stamps `data-theme` before anything renders.
 * Feature detection happens here and only here — `clipboard.canRead` / `canWrite` are
 * fields, read once, so the toolbar asks a question instead of catching an exception.
 */
export function createServices(options: ServicesOptions = {}): Services {
  const storage = createStorageService();
  return {
    storage,
    clipboard: createClipboardService(),
    theme: createThemeService({ storage }),
    analyzer: createAnalyzerService(options.analyzer ?? {}),
  };
}

/**
 * The null object: every method a no-op or the allowed default, and NOT ONE LINE OF DOM
 * (ADR-0015, ADR-0019).
 *
 * That last property is the point of the function. Nothing below touches `document`,
 * `navigator`, `localStorage` or `matchMedia` — not even to feature-detect them — so a
 * command test built on this runs in the bare-Node `logic` Vitest project, and gate 3 stays
 * meaningful instead of every test quietly needing jsdom.
 *
 *   clipboard  absent: both flags `false`, both methods reject `unsupported`. A test that
 *              wants a working one passes `createFakeClipboard()` from
 *              `test/helpers/nullServices.ts`.
 *   storage    a real, working `Map`. Nothing survives the test; nothing is a no-op either,
 *              because "write a preference then read it back" is the behaviour under test.
 *   theme      memory preference, no OS signal, no element stamped.
 *   analyzer   the REAL engine, inline, never a Worker. A command that formats has to
 *              actually format; a stub returning an empty report would make every command
 *              test agree with itself and with nothing else.
 *
 * `overrides` is applied per service, so replacing just the analyzer with
 * `createSyncAnalyzer()` is one word.
 */
export function createNullServices(overrides?: Partial<Services>): Services {
  const base: Services = {
    clipboard: createNullClipboardService(),
    storage: createMemoryStorageService(),
    theme: createDetachedThemeService(),
    analyzer: createAnalyzerService({ createWorker: null }),
  };
  return { ...base, ...overrides };
}
